import { media } from "./puduMedia";

export type PuduCategory =
  | "Landscaping & Turf Care"
  | "Logistics & Material Handling"
  | "Commercial Cleaning";

export interface PuduSpec {
  label: string;
  value: string;
}

export interface PuduFeature {
  title: string;
  body: string;
  media?: string;
  mediaType?: "image" | "video";
  icon?: string;
}

export interface PuduGalleryItem {
  src: string;
  type: "image" | "video";
  caption: string;
}

export interface PuduVariant {
  id: string;
  label: string;
  modelId: string;
  forkOuterWidth: string;
}

export interface PuduProduct {
  slug: string;
  name: string;
  subtitle: string;
  category: PuduCategory;
  headline: string;
  summary: string;
  cardSummary: string;
  heroImage: string;
  cardImage: string;
  mainVideo: string;
  poster: string;
  metrics: { label: string; value: string }[];
  features: PuduFeature[];
  gallery: PuduGalleryItem[];
  specs: PuduSpec[];
  applications: { title: string; body: string; image?: string }[];
  notes: string[];
  faqs: { q: string; a: string }[];
  variants?: PuduVariant[];
  sourceUrl: string;
  verifiedOn: string;
  related: string[];
}

const m = media;

export const puduProducts: PuduProduct[] = [
  {
    slug: "gt3",
    name: "GT3",
    subtitle: "Commercial Compact Robotic Mower",
    category: "Landscaping & Turf Care",
    headline: "Cut, collect and sweep in a single autonomous pass.",
    summary:
      "GT3 is a compact commercial mower built for grounds crews that are short on labor and long on acreage. It cuts and vacuum-collects clippings in one pass, sweeps hard walkways with the blades off, empties its own hopper and returns to the exact point where it stopped.",
    cardSummary:
      "Compact commercial mower that cuts, collects and sweeps autonomously, then empties its own hopper.",
    heroImage: m("s1_head_pc_4ff68ee999.webp"),
    cardImage: m("GT_3_ba06d2f197.png"),
    mainVideo: m("GT_3_EN_4775061562.mp4"),
    poster: m("GT_3_EN_4775061562-poster.jpg"),
    metrics: [
      { label: "Cut width", value: "914.4 mm (36 in)" },
      { label: "Coverage per charge", value: "20,000 m² (5 acres)" },
      { label: "Runtime", value: "4–6 hours" },
    ],
    features: [
      {
        title: "One-pass cutting & collection",
        body: "Rotary mowing paired with vacuum collection lifts clippings as it cuts, so turf is finished in a single pass instead of a mow-then-blow cycle.",
        media: m("S3_9a91ecea8c.webp"),
        icon: m("S2_1_2a159773a5.svg"),
      },
      {
        title: "Mowing & sweeping",
        body: "With the blades disengaged, GT3 sweeps hard walkways and hard-surface edges using dual edge brushes — one machine covering turf and pavement.",
        media: m("S4_c60403abec.webp"),
        icon: m("S2_2_6f395fbaaf.svg"),
      },
      {
        title: "Auto-dump & task resumption",
        body: "The hopper empties automatically and the robot returns to its breakpoint, so long tasks continue without an operator standing by.",
        media: m("S5_ea441dba46.webp"),
        icon: m("S2_3_b07704513b.svg"),
      },
      {
        title: "Auto mapping & border recognition",
        body: "Single-lap mapping distinguishes turf from pavement. Exclusion zones and transit routes are editable after the map is built.",
        media: m("S6_1_473f4e2e83.webp"),
        icon: m("S2_4_183d41dc39.svg"),
      },
      {
        title: "Autonomous cross-zone transit",
        body: "GT3 travels between mapped zones on its own, chaining separate lawns and islands into one scheduled task.",
        media: m("S6_2_f9a9595ed5.webp"),
        icon: m("S2_5_694e8d9517.svg"),
      },
      {
        title: "Edge sweeping",
        body: "Dual edge brushes pull debris out of the perimeter line so borders finish clean.",
        media: m("S6_3_1c7f4aab79.webp"),
        icon: m("S2_6_4ac9ba857f.svg"),
      },
      {
        title: "Adaptive obstacle response",
        body: "Manufacturer-stated detection and response to pedestrians, vehicles and obstacles during operation.",
        media: m("S7_8eba56fc74.webp"),
      },
      {
        title: "IP66 build and layered safety systems",
        body: "IP66-rated chassis with emergency stops, blade interlocks, lift and tilt braking, and status indicators.",
        media: m("S8_f4232fdf46.webp"),
      },
    ],
    gallery: [
      { src: m("S9_2502e32a2a.webp"), type: "image", caption: "GT3 product overview" },
      { src: m("S1_4ee05db53f.webp"), type: "image", caption: "GT3 working on commercial turf" },
      { src: m("GT_3_ba06d2f197.png"), type: "image", caption: "GT3 studio view" },
    ],
    specs: [
      { label: "Dimensions", value: "1602 × 946.6 × 1203 mm (63 × 37.3 × 47.4 in)" },
      { label: "Weight including batteries", value: "450 kg (992 lb)" },
      { label: "Cut width", value: "914.4 mm (36 in)" },
      { label: "Cut height", value: "26–114 mm (1–4.5 in)" },
      { label: "Maximum mowing speed", value: "11 km/h (6.7 mph, 3 m/s)" },
      { label: "Battery", value: "13 kWh" },
      { label: "Runtime", value: "4–6 hours" },
      { label: "Coverage per charge", value: "20,000 m² (5 acres, 2 ha)" },
      { label: "Standard charge", value: "4 hours at 3 kW" },
      { label: "Optional fast charge", value: "2 hours at 6 kW" },
      { label: "Mowing slope", value: "Not published" },
      { label: "Hourly mowing rate", value: "Not published" },
      { label: "Protection rating", value: "IP66" },
    ],
    applications: [
      { title: "Corporate campuses", body: "Mixed turf and walkway maintenance on a repeating schedule." },
      { title: "Municipal parks", body: "Multi-zone properties where transit between lawns wastes crew hours." },
      { title: "Schools and churches", body: "Smaller commercial acreage with pedestrian traffic during the day." },
      { title: "Property management", body: "Grounds contracts that need consistent output with a smaller crew." },
    ],
    notes: [
      "Mowing slope and hourly coverage are not published for GT3. Neil AI does not substitute GT5 or GT7 figures.",
      "Manufacturer-stated performance. Actual results depend on turf, terrain, moisture and configuration.",
    ],
    faqs: [
      {
        q: "Does GT3 empty its own grass hopper?",
        a: "Yes. The manufacturer specifies automatic hopper emptying with return to the breakpoint so the task resumes where it stopped.",
      },
      {
        q: "Can GT3 clean hard surfaces?",
        a: "It can sweep walkways with the blades disengaged using its dual edge brushes. It is not a scrubber-dryer.",
      },
      {
        q: "What slope can GT3 handle?",
        a: "A mowing slope is not published for GT3. Slope suitability is confirmed during a site assessment.",
      },
      {
        q: "What does a charge cover?",
        a: "Manufacturer-published coverage is 20,000 m² (5 acres) per charge, with 4–6 hours of runtime.",
      },
    ],
    sourceUrl: "https://www.pudurobotics.com/en/products/pudu-gt3",
    verifiedOn: "2026-09-07",
    related: ["gt5", "gt7"],
  },

  {
    slug: "gt5",
    name: "GT5",
    subtitle: "Commercial Robotic Mower",
    category: "Landscaping & Turf Care",
    headline: "60-inch autonomous mowing for large commercial turf.",
    summary:
      "GT5 is a wide-deck commercial mower for properties measured in acres. It cuts, vacuums and collects, empties its hopper automatically, and geofences work areas from satellite or online maps with edge approach within 5 cm.",
    cardSummary:
      "Wide-deck 60-inch commercial mower with automatic collection, geofenced zones and 8–10 hour runtime.",
    heroImage: m("s1_head_pc_621c5f87c8.webp"),
    cardImage: m("GT_5_8ee5f1cc0b.png"),
    mainVideo: m("PUDU_GT_5_EN_1080p_bda11133cc.mp4"),
    poster: m("PUDU_GT_5_EN_1080p_bda11133cc-poster.jpg"),
    metrics: [
      { label: "Cut width", value: "1.52 m (60 in)" },
      { label: "Mowing rate", value: "8,000 m²/h (2 acres/h)" },
      { label: "Mowing slope", value: "20°" },
    ],
    features: [
      {
        title: "One-pass cutting & collection",
        body: "Rotary cutting, vacuuming and collection work together so clippings leave with the pass instead of being blown afterward.",
        media: m("S3_b592761d1a.webp"),
        icon: m("S2_1_03f592e3f6.svg"),
      },
      {
        title: "Auto-dump & task resumption",
        body: "Automatic hopper emptying and breakpoint return keep long jobs moving without an operator waiting on the machine.",
        media: m("S4_f7523e2299.webp"),
        icon: m("S2_2_9e7368d393.svg"),
      },
      {
        title: "Extended run-time",
        body: "A 26 kWh pack supports 8–10 hours of work and 65,000 m² (16 acres) per charge.",
        media: m("S5_42508be632.webp"),
        icon: m("S2_3_0fe886cf4f.svg"),
      },
      {
        title: "60-inch wide-area mowing",
        body: "A 1.52 m cut width targets 8,000 m² per hour on open commercial turf.",
        media: m("S6_9902f0749a.webp"),
      },
      {
        title: "Autonomous cross-zone transit",
        body: "GT5 moves between mapped zones on its own, so separated lawns run as one task.",
        media: m("S7_be2e5b33f5.webp"),
        icon: m("S2_5_a16696c4a1.svg"),
      },
      {
        title: "Auto mapping & rapid deployment",
        body: "Autonomous mapping plus satellite and online-map geofencing shortens commissioning on large sites.",
        media: m("S8_c1104c07da.webp"),
        icon: m("S2_4_5c22583d5a.svg"),
      },
      {
        title: "Precision tracking & edge trim",
        body: "Edge approach within 5 cm reduces the amount of hand trimming left behind.",
        media: m("S9_8f9feb92fe.webp"),
      },
      {
        title: "360° omnidirectional awareness",
        body: "RGB, RGBD and LiDAR perception cover the machine's surroundings during operation.",
        media: m("S10_1_e62cdb7111.webp"),
        icon: m("S2_6_02b9063adb.svg"),
      },
      {
        title: "Adaptive obstacle response",
        body: "Manufacturer-stated adaptive response to obstacles encountered on the route.",
        media: m("S10_2_5b07b069a8.webp"),
      },
      {
        title: "Active anti-fall protection",
        body: "Drop-off detection and configurable no-go zones restrict work near hazards.",
        media: m("S11_2970361930.webp"),
      },
      {
        title: "All-metal chassis & IP66 protection",
        body: "A metal chassis with IP66 rating for outdoor commercial duty.",
        media: m("S12_c7b14bf19c.webp"),
      },
    ],
    gallery: [
      { src: m("S13_33310d1107.png"), type: "image", caption: "GT5 product overview" },
      { src: m("S1_h5_51ad7d3665.webp"), type: "image", caption: "GT5 on commercial turf" },
      { src: m("GT_5_8ee5f1cc0b.png"), type: "image", caption: "GT5 studio view" },
    ],
    specs: [
      { label: "Dimensions", value: "1910 × 1586 × 1293 mm (75.2 × 62.44 × 50.91 in)" },
      { label: "Weight including batteries", value: "850 kg (1,873.93 lb)" },
      { label: "Cut width", value: "1.52 m (60 in)" },
      { label: "Cut height", value: "26–152 mm (1–6 in)" },
      { label: "Target mowing rate", value: "8,000 m²/h (2 acres/h)" },
      { label: "Maximum mowing speed", value: "11 km/h (6.7 mph, 3 m/s)" },
      { label: "Mowing slope", value: "20°" },
      { label: "Battery", value: "26 kWh" },
      { label: "Runtime", value: "8–10 hours" },
      { label: "Coverage per charge", value: "65,000 m² (16 acres, 6.5 ha)" },
      { label: "Standard charge", value: "8 hours at 3 kW" },
      { label: "Optional fast charge", value: "4 hours at 6 kW" },
      { label: "Protection rating", value: "IP66" },
    ],
    applications: [
      { title: "Sports complexes", body: "Multi-field sites needing repeatable output every week." },
      { title: "Large corporate campuses", body: "Acreage that currently consumes most of a crew's week." },
      { title: "Municipal grounds", body: "Parks and civic properties with mapped, repeating routes." },
      { title: "Golf course roughs and surrounds", body: "Open areas where wide-deck coverage pays back fastest." },
    ],
    notes: [
      "The source page displays the cut-height maximum as \"15 2 mm\"; Neil AI records 152 mm, supported by the published 6-inch equivalent.",
      "Manufacturer-stated performance. Actual results depend on turf, terrain, moisture and configuration.",
    ],
    faqs: [
      { q: "How much turf does GT5 cover per charge?", a: "Manufacturer-published coverage is 65,000 m² (16 acres) per charge at a target rate of 8,000 m² per hour." },
      { q: "How close can GT5 mow to an edge?", a: "The manufacturer specifies edge approach within 5 cm." },
      { q: "How are work areas defined?", a: "Through autonomous mapping plus geofencing drawn on satellite or online maps, with no-go zones." },
      { q: "What slope is published?", a: "20° mowing slope." },
    ],
    sourceUrl: "https://www.pudurobotics.com/en/products/pudu-gt5",
    verifiedOn: "2026-09-07",
    related: ["gt3", "gt7"],
  },

  {
    slug: "gt7",
    name: "GT7",
    subtitle: "Precision Robotic Reel Mower",
    category: "Landscaping & Turf Care",
    headline: "Reel-cut precision and striping, run autonomously.",
    summary:
      "GT7 is a four-wheel-drive robotic reel mower for turf that is judged on finish. RTK positioning at ±2.5 cm, adjustable mowing directions and offsets produce controlled striping, while three independently floating reel units follow ground contours.",
    cardSummary:
      "Four-wheel-drive robotic reel mower with RTK precision, pattern control and 10–12 hour runtime.",
    heroImage: m("s1_head_pc_799074e916.webp"),
    cardImage: m("GT_7_319fdff8c3.png"),
    mainVideo: m("PUDU_GT_7_EN_1080_P_24d21e241a.mp4"),
    poster: m("PUDU_GT_7_EN_1080_P_24d21e241a-poster.jpg"),
    metrics: [
      { label: "RTK accuracy", value: "±2.5 cm" },
      { label: "Cut height", value: "5–50 mm" },
      { label: "Mowing slope", value: "30°" },
    ],
    features: [
      {
        title: "Multi-sensor fusion & precision positioning",
        body: "RTK, LiDAR and vision positioning combine for ±2.5 cm RTK accuracy on open turf.",
        media: m("S3_ec9fbe7bd7.webp"),
        icon: m("S2_1_eef66d6adf.svg"),
      },
      {
        title: "Precision mowing & advanced pattern control",
        body: "Adjustable mowing directions and offsets produce controlled striping patterns across a mapped area.",
        media: m("S4_c88937d4df.mp4"),
        mediaType: "video",
        icon: m("S2_2_967f87a075.svg"),
      },
      {
        title: "Autonomous cross-zone transit",
        body: "Mapped transit routes let GT7 move between work zones without being trailered.",
        media: m("S5_4ec29a7468.webp"),
        icon: m("S2_3_d84782694d.svg"),
      },
      {
        title: "Broad coverage, tight-space agility",
        body: "A 1600 mm cut width with a lifted-deck profile of 1500 mm for constrained gates and paths.",
        media: m("S6_1_492d34d588.mp4"),
        mediaType: "video",
        icon: m("S2_4_e4aadc9f96.svg"),
      },
      {
        title: "4WD traction on uneven turf",
        body: "Four-wheel drive supports work on undulating ground and a published 30° mowing slope.",
        media: m("S6_2_d9669bf24a.webp"),
        icon: m("S2_5_9e32fe3760.svg"),
      },
      {
        title: "Low-noise electric operation",
        body: "Fully electric operation supports early-morning and noise-sensitive schedules.",
        media: m("S7_86a5a00b14.webp"),
        icon: m("S2_7_c146c2ab6b.svg"),
      },
      {
        title: "Extended run-time",
        body: "A 26 kWh pack supports 10–12 hours and up to 70,000 m² (17 acres) per charge.",
        media: m("S8_9bc3dc5fe8.webp"),
        icon: m("S2_6_27af99968f.svg"),
      },
      {
        title: "Dual-mode flexibility",
        body: "Switch between mowing and collection modes depending on the finish required.",
        media: m("S9_157ef77427.mp4"),
        mediaType: "video",
      },
      {
        title: "Boosted vacuum collection",
        body: "A dual-fan vacuum system lifts clippings, with hopper-full sensing and task resumption.",
        media: m("S10_c8a3ee345e.mp4"),
        mediaType: "video",
      },
      {
        title: "Contour-following floating cutting units",
        body: "Three independently floating reel units track ground contours to reduce scalping.",
        media: m("S11_ce593ad114.mp4"),
        mediaType: "video",
        icon: m("S2_8_8346545243.svg"),
      },
      {
        title: "360° omnidirectional awareness",
        body: "RGB, RGBD, ultrasonic and LiDAR perception with stop, wait or bypass responses.",
        media: m("S12_1_e097f84cc9.mp4"),
        mediaType: "video",
      },
      {
        title: "Adaptive obstacle avoidance",
        body: "Obstacle handling adapts to what the perception stack classifies on the route.",
        media: m("S12_2_9840061ea0.webp"),
      },
      {
        title: "Remote fleet management",
        body: "Remote location, progress and health monitoring with fault notifications.",
        media: m("S12_3_e453731acc.webp"),
      },
      {
        title: "Tool-free dial-in height adjustment",
        body: "Cut height is set without tools between 5 mm and 50 mm.",
        media: m("S13_75f02cad9e.webp"),
      },
    ],
    gallery: [
      { src: m("S14_b6ef147a61.png"), type: "image", caption: "GT7 product overview" },
      { src: m("S1_H5_a7e33cc92e.webp"), type: "image", caption: "GT7 on fine turf" },
      { src: m("GT_7_319fdff8c3.png"), type: "image", caption: "GT7 studio view" },
    ],
    specs: [
      { label: "Dimensions", value: "1950 × 1600 × 1400 mm (76.8 × 63.0 × 55.1 in)" },
      { label: "Dimensions, lifted", value: "1950 × 1500 × 1400 mm (76.8 × 59.1 × 55.1 in)" },
      { label: "Weight including batteries", value: "900 kg (1,984 lb)" },
      { label: "Mowing rate", value: "8,000 m²/h (2 acres/h)" },
      { label: "Cut width", value: "1600 mm (62 in)" },
      { label: "Reel blades", value: "8 or 11" },
      { label: "Cut height", value: "5–50 mm (0.197–1.97 in)" },
      { label: "Maximum mowing speed", value: "11 km/h (6.7 mph, 3 m/s)" },
      { label: "Battery", value: "26 kWh" },
      { label: "Runtime", value: "10–12 hours" },
      { label: "Coverage per charge", value: "70,000 m² (17 acres, 7 ha)" },
      { label: "Charging", value: "8 h at 3 kW standard / 4 h at 6 kW optional" },
      { label: "Mowing slope", value: "30°" },
      { label: "Positioning accuracy", value: "RTK ±2.5 cm" },
    ],
    applications: [
      { title: "Golf fairways and approaches", body: "Reel cutting where finish quality is judged closely." },
      { title: "Stadium and premium sports turf", body: "Controlled striping patterns on show surfaces." },
      { title: "Estate and resort lawns", body: "Fine turf maintained on a tight height band." },
      { title: "Turf management contractors", body: "Crews standardizing finish across multiple properties." },
    ],
    notes: [
      "Maximum runtime and coverage figures are published for non-collection mode.",
      "Automatic hopper dumping and an IP66 rating are not established for GT7 by the source page; GT7 uses hopper-full detection with task resumption.",
      "Manufacturer-stated performance. Actual results depend on turf, terrain, moisture and configuration.",
    ],
    faqs: [
      { q: "Does GT7 empty its hopper automatically?", a: "The source page establishes hopper-full sensing and task resumption. Automatic dumping is not stated for GT7, unlike GT3 and GT5." },
      { q: "How is GT7's maximum coverage measured?", a: "The 70,000 m² per charge and 10–12 hour figures are published for non-collection mode." },
      { q: "Can GT7 stripe turf?", a: "Yes — mowing directions and offsets are adjustable to produce patterns." },
      { q: "Is GT7 rated IP66?", a: "An IP rating is not published for GT7 on the source page, so Neil AI does not claim one." },
    ],
    sourceUrl: "https://www.pudurobotics.com/en/products/pudu-gt7",
    verifiedOn: "2026-09-07",
    related: ["gt5", "gt3"],
  },

  {
    slug: "mp2000",
    name: "MP2000",
    subtitle: "Autonomous Pallet Handling Robot",
    category: "Logistics & Material Handling",
    headline: "2,000 kg pallet moves, handled autonomously at low lift height.",
    summary:
      "MP2000 is an autonomous pallet mover for manufacturing supply, warehouse pallet flow and distribution transfers. It recognizes load carriers and free storage space, adapts to pallets that are out of position, and inserts its forks in about 20 seconds. It transports pallets at low lift height — it is not a high-rack stacker.",
    cardSummary:
      "Autonomous 2,000 kg pallet mover with AI load-carrier recognition, fleet orchestration and 20-second fork insertion.",
    heroImage: m("s1_head_pc_7b6f7c63ce.webp"),
    cardImage: m("s1_9a92ce6190.png"),
    mainVideo: m("PUDU_MP_2000_1080_aa11ea505a.mp4"),
    poster: m("PUDU_MP_2000_1080_aa11ea505a-poster.jpg"),
    metrics: [
      { label: "Payload", value: "2,000 kg (4,409.25 lb)" },
      { label: "Fork insertion", value: "About 20 seconds" },
      { label: "Runtime", value: "6 h loaded / 12 h unloaded" },
    ],
    variants: [
      { id: "standard", label: "Standard", modelId: "MPID01-M", forkOuterWidth: "620 mm (24.41 in)" },
      { id: "narrow", label: "Narrow Fork", modelId: "MPID01-N", forkOuterWidth: "550 mm (21.65 in)" },
    ],
    features: [
      {
        title: "Plug-and-play, rapid deployment",
        body: "Rapid mapping shortens commissioning, so a site can be running tasks without a fixed-infrastructure retrofit.",
        media: m("S3_6d1c591d64.mp4"),
        mediaType: "video",
        icon: m("S2_1_ff1c35d208.svg"),
      },
      {
        title: "AI-native autonomous operation",
        body: "Onboard perception drives pallet handling decisions instead of relying on rigid, pre-programmed positions.",
        media: m("s4_1_3ac0541ae2.webp"),
        icon: m("S2_2_dc0e2577b8.svg"),
      },
      {
        title: "AI load carrier recognition",
        body: "Recognizes three-runner and perimeter-base pallets, and can learn nonstandard carriers used on your site.",
        media: m("S4_2_en_a36dcf51a5.webp"),
      },
      {
        title: "AI storage detection",
        body: "Detects occupancy and free storage positions before placing a pallet.",
        media: m("s4_4_1651fc201b.mp4"),
        mediaType: "video",
      },
      {
        title: "Adaptive alignment, precise fork engagement",
        body: "Adapts to pallets misaligned by up to ±15 cm and ±15°, so loads do not have to be perfectly staged.",
        media: m("s4_5_937b888963.mp4"),
        mediaType: "video",
      },
      {
        title: "Intelligent clearance assessment",
        body: "Evaluates clearance around the target before committing to a pick or drop.",
        media: m("s4_6_a37186e672.webp"),
      },
      {
        title: "Intelligent fleet orchestration",
        body: "Coordinates with other units, including offline coordination, so multiple robots share the same floor.",
        media: m("s5_1_14b9012651.mp4"),
        mediaType: "video",
        icon: m("S2_3_c570d2e348.svg"),
      },
      {
        title: "20-second fork insertion",
        body: "Fast pickup keeps cycle times short on repetitive transfer routes.",
        media: m("s6_37ac0cb9a3.webp"),
        icon: m("S2_4_7b11f24a3b.svg"),
      },
      {
        title: "More storage in narrow aisles",
        body: "Right-angle aisle requirement of ≤2000 mm for a 1200×1000 pallet and ≤1960 mm for 1200×800, with pallet spacing down to 150 mm.",
        media: m("S7_4a8f4d1bd8.webp"),
      },
      {
        title: "Omni-sense safety",
        body: "Depth cameras and 3D LiDAR drive obstacle response around the machine.",
        media: m("S8_1_21d90446d8.webp"),
        icon: m("S2_5_5d55d862e6.svg"),
      },
      {
        title: "Flexible dual-mode operation",
        body: "Runs autonomously or under manual control when an operator needs to intervene.",
        media: m("S9_1_980d6f4ff4.webp"),
      },
      {
        title: "IoT integration & control",
        body: "Dispatch by app, API, voice or button, with optional elevator and door integration.",
        media: m("S10_1en_b2e287ef7a.webp"),
        icon: m("S2_6_d4c64c8bbc.svg"),
      },
    ],
    gallery: [
      { src: m("S14_fb76ac7a7e.webp"), type: "image", caption: "MP2000 Standard product overview" },
      { src: m("s4_3_0ee486f642.webp"), type: "image", caption: "Load carrier recognition" },
      { src: m("S8_2_8eba548cd8.webp"), type: "image", caption: "Omni-sense safety coverage" },
      { src: m("S8_3_4a74df0309.webp"), type: "image", caption: "Omni-sense safety detail" },
      { src: m("S9_2_EN_a768f49cef.webp"), type: "image", caption: "Dual-mode operation" },
      { src: m("S10_2_9dc93be730.webp"), type: "image", caption: "IoT integration" },
      { src: m("S10_3_acbca9d9e4.webp"), type: "image", caption: "Dispatch and control" },
      { src: m("S10_4_e51252554e.webp"), type: "image", caption: "System integration" },
      { src: m("s1_head_H5_5fdfc52c9b.webp"), type: "image", caption: "MP2000 in operation" },
    ],
    specs: [
      { label: "Dimensions", value: "1585 × 910 × 1870 mm (62.40 × 35.83 × 73.62 in)" },
      { label: "Payload", value: "2,000 kg (4,409.25 lb)" },
      { label: "Lift / lowered forks", value: "200 ± 5 mm / 80 ± 5 mm" },
      { label: "Right-angle aisle", value: "≤2000 mm for 1200×1000 pallet; ≤1960 mm for 1200×800 (1200 dimension along forks)" },
      { label: "Pallet spacing", value: "Down to 150 mm" },
      { label: "Navigation", value: "3D LiDAR-SLAM; VSLAM" },
      { label: "Threshold / gap", value: "10 mm / 35 mm" },
      { label: "Grade, loaded / unloaded", value: "3° / 8°" },
      { label: "Speed, loaded / unloaded", value: "1.2 m/s / 1.6 m/s" },
      { label: "Runtime, max load / unloaded", value: "6 h / 12 h" },
      { label: "Charging", value: "About 2 h at 200–240 VAC; 3.2 h at 100–120 VAC" },
      { label: "Alignment adaptation", value: "±15 cm / ±15°" },
    ],
    applications: [
      { title: "Manufacturing supply", body: "Line-side replenishment between staging and production.", image: m("s11_1_08815059fb.webp") },
      { title: "Warehouse pallet movement", body: "Repetitive pallet transfer across mapped floor routes.", image: m("s11_2_2463551af4.webp") },
      { title: "Distribution transfers", body: "Inbound and outbound staging moves between dock and storage.", image: m("s11_3_e3aa8420ed.webp") },
    ],
    notes: [
      "MP2000 transports pallets at low lift height (200 ± 5 mm). It is not a high-rack stacker and must not be specified as one.",
      "Pallet type, aisle geometry and floor condition are validated during a site assessment before deployment.",
    ],
    faqs: [
      { q: "What is the difference between the Standard and Narrow Fork variants?", a: "Fork outer width and model ID: Standard MPID01-M at 620 mm (24.41 in) and Narrow Fork MPID01-N at 550 mm (21.65 in). All other published specifications are shared." },
      { q: "Can MP2000 place pallets into racking?", a: "No. Published lift height is 200 ± 5 mm — low-lift pallet transport, not high-bay stacking." },
      { q: "Does it handle pallets that are not perfectly positioned?", a: "The manufacturer specifies adaptation to misalignment up to ±15 cm and ±15°." },
      { q: "How is it dispatched?", a: "By app, API, voice or button, with optional elevator and door integration." },
    ],
    sourceUrl: "https://www.pudurobotics.com/en/products/mp2000",
    verifiedOn: "2026-09-07",
    related: ["et1", "gt5"],
  },

  {
    slug: "et1",
    name: "ET1",
    subtitle: "Compact Scrubber-Dryer Robot",
    category: "Commercial Cleaning",
    headline: "Four cleaning modes and an eight-function dock in a compact footprint.",
    summary:
      "ET1 sweeps, scrubs, vacuums and dust-mops, cleaning its roller in real time as it works. Its dock handles charging, roller washing, squeegee rinsing, spin-drying, refill, wastewater drainage, detergent dosing and water heating — so the daily routine stops depending on staff time.",
    cardSummary:
      "Compact 4-in-1 scrubber-dryer with real-time roller self-cleaning and an eight-function dock.",
    heroImage: m("s1_head_pc_68fc3db77e.webp"),
    cardImage: m("s1_ET_1_9beae62820.png"),
    mainVideo: m("ET_1_En_cb773c5eee.mp4"),
    poster: m("ET_1_En_cb773c5eee-poster.jpg"),
    metrics: [
      { label: "Cleaning rate", value: "300–720 m²/h" },
      { label: "Runtime", value: "2.5–4 h" },
      { label: "Clearance", value: "50 cm wide / 60 cm high" },
    ],
    features: [
      {
        title: "4-in-1 versatile cleaning",
        body: "Sweeping, scrubbing, vacuuming and dust-mopping in one machine, so one pass replaces several manual routines.",
        media: m("s3_1930696c7d.webp"),
        icon: m("s2_1_e3e0396818.svg"),
      },
      {
        title: "Real-time roller self-cleaning",
        body: "The roller cleans itself as it works, at up to 800 rpm with 5,000 Pa of downward pressure.",
        media: m("S4_EN_6ab082c9c7.mp4"),
        mediaType: "video",
        icon: m("s2_2_01b9b73844.svg"),
      },
      {
        title: "Up to 85 °C hot scrubbing",
        body: "Dock-supplied water with onboard heating raises scrubbing temperature for tougher soil.",
        media: m("s5_36783cb2c9.mp4"),
        mediaType: "video",
      },
      {
        title: "Streak-free in one pass",
        body: "Water recovery lifts moisture behind the scrub head so floors finish in a single pass.",
        media: m("s6_b4c3c424cf.webp"),
        icon: m("s2_6_cd838c57d9.svg"),
      },
      {
        title: "Auto dust collection",
        body: "Dust is collected into the dock so the 2.5 L dustbox is not a daily task.",
        media: m("s8_132710d11f.mp4"),
        mediaType: "video",
      },
      {
        title: "AI Magic Cleaning",
        body: "Cleaning behaviour adjusts to what the machine detects on the floor rather than running one fixed profile.",
        media: m("s9_d4cbd617b9.webp"),
        icon: m("s2_5_c9681f231d.svg"),
      },
      {
        title: "Auto mapping, one-click start",
        body: "Automatic mapping gets the route built quickly, then staff launch it with a single action.",
        media: m("s10_EN_c09ca45caf.webp"),
        icon: m("s2_8_59985d7328.svg"),
      },
      {
        title: "AI floor surface and stain recognition",
        body: "Recognizes floor surfaces and stains, lifts the roller at carpet edges and over spills.",
        media: m("s11_1_7a66d2f9a3.webp"),
      },
      {
        title: "Compact design, agile navigation",
        body: "A 500 × 380 mm footprint clears 50 cm-wide and 60 cm-high gaps for tight retail and back-of-house layouts.",
        media: m("s14_f0ca78b380.webp"),
        icon: m("s2_7_36bf526238.svg"),
      },
      {
        title: "Dual-system design",
        body: "Separate Admin and User modes keep configuration and daily operation apart.",
        media: m("s15_1_EN_95ab883d5b.webp"),
      },
      {
        title: "Built-in training system",
        body: "Onboard training helps new staff run the machine without a specialist on site.",
        media: m("s15_2_EN_d0de323db9.webp"),
      },
      {
        title: "Proactive maintenance alerts",
        body: "The robot raises maintenance items before they interrupt a shift.",
        media: m("s15_3_EN_c103cd386a.webp"),
      },
      {
        title: "Detachable, washable components",
        body: "Quick-release parts make the daily and weekly service routine straightforward.",
        media: m("S16_8eca608c48.webp"),
      },
    ],
    gallery: [
      { src: m("s17_parameters_robot_5f3e64d7b5.png"), type: "image", caption: "ET1 product overview" },
      { src: m("s1_head_H5_f0791d0b81.webp"), type: "image", caption: "ET1 in a commercial space" },
      { src: m("s11_2_0414d79180.webp"), type: "image", caption: "Floor and stain recognition detail" },
      { src: m("s1_ET_1_9beae62820.png"), type: "image", caption: "ET1 studio view" },
    ],
    specs: [
      { label: "Dimensions", value: "500 × 380 × 580 mm (19.69 × 14.96 × 22.83 in)" },
      { label: "Weight", value: "Approximately 45 kg (99.2 lb)" },
      { label: "Speed", value: "Maximum 1 m/s (3.28 ft/s)" },
      { label: "Cleaning rate", value: "300–720 m²/h (3,229–7,750 ft²/h)" },
      { label: "Scrub / dust-mop width", value: "30 cm (11.81 in)" },
      { label: "Sweep / vacuum width", value: "39 cm (15.35 in)" },
      { label: "Clean / wastewater tanks", value: "8 L / 7.2 L (2.11 / 1.9 gal)" },
      { label: "Dustbox", value: "2.5 L (0.66 gal)" },
      { label: "Clearance, width / height", value: "50 cm / 60 cm (19.69 / 23.62 in)" },
      { label: "Runtime", value: "2.5–4 h" },
      { label: "Battery", value: "35 Ah" },
      { label: "Charging", value: "Approximately 3 h" },
      { label: "Maximum roller speed", value: "800 rpm" },
      { label: "Downward pressure", value: "5,000 Pa" },
      { label: "Water heating", value: "Up to 85 °C, dock-supplied with onboard heating" },
    ],
    applications: [
      { title: "Retail and convenience", body: "Tight aisles and daytime cleaning around customers." },
      { title: "Restaurants and cafés", body: "Back-of-house and dining floors between services." },
      { title: "Clinics and offices", body: "Repeatable nightly routines in compact footprints." },
      { title: "Hotels and senior living", body: "Corridors and common areas cleaned on schedule." },
    ],
    notes: [
      "The 21.5-inch advertising display is optional equipment, not standard. Dimensions and clearances for a display-equipped configuration are not confirmed on the source page.",
      "Some manufacturer custom-wrap marketing examples are AI-generated. Neil AI keeps those out of the factual product gallery.",
      "Confirm dock inclusion and utilities, display-configuration clearances and floor compatibility during a site assessment. No zero-maintenance or universal surface-safety promise is made.",
    ],
    faqs: [
      { q: "What does the dock do?", a: "Eight functions: charging, roller washing, squeegee rinsing, roller spin-drying, clean-water refill, wastewater drainage, detergent dosing and water heating." },
      { q: "Is the advertising display included?", a: "No. The 21.5-inch display is optional. Clearances for a display-equipped machine are not confirmed by the source page." },
      { q: "Will ET1 work on my floors?", a: "Floor compatibility is confirmed during a site assessment. No universal surface-safety claim is made." },
      { q: "How much can ET1 clean?", a: "Manufacturer-published cleaning rate is 300–720 m²/h with 2.5–4 hours of runtime." },
    ],
    sourceUrl: "https://www.pudurobotics.com/en/products/et1",
    verifiedOn: "2026-09-07",
    related: ["mp2000", "gt3"],
  },
];

export const getPuduProduct = (slug?: string) =>
  puduProducts.find((p) => p.slug === slug);

export const et1DockFunctions = [
  { title: "Auto charging", icon: media("s7_2_be93393e48.svg") },
  { title: "Automatic roller cleaning", icon: media("s7_8_5bafe039b3.svg") },
  { title: "Auto squeegee rinsing", icon: media("s7_1_dd0cd3436e.svg") },
  { title: "Automatic roller spin-drying", icon: media("s7_7_581a27c3e0.svg") },
  { title: "Auto clean water refill", icon: media("s7_3_73ce75a4c4.svg") },
  { title: "Auto wastewater drainage", icon: media("s7_6_69860d7eaf.svg") },
  { title: "Auto agent dosing", icon: media("s7_5_c3bc73667c.svg") },
  { title: "Auto water heating", icon: media("s7_4_60d3463529.svg") },
];

export const et1AdaptiveCleaning = {
  video: media("s12_4_15e8bf88c7.mp4"),
  items: [
    { title: "Roller speed increased", icon: media("s12_1_fe635aacd7.svg") },
    { title: "Travel speed reduced", icon: media("s12_2_6c92b9f56b.svg") },
    { title: "Water output increased", icon: media("s12_3_ce69f3219b.svg") },
  ],
};

export const et1DockVideo = media("s7_9_78362ce2cd.mp4");
export const et1DisplayImage = media("s13_1_037fc04df3.webp");

export const mowerComparison: {
  label: string;
  gt3: string;
  gt5: string;
  gt7: string;
}[] = [
  { label: "Cutting method", gt3: "Rotary + vacuum collection", gt5: "Rotary + vacuum collection", gt7: "Reel (8 or 11 blades) + dual-fan vacuum" },
  { label: "Cut width", gt3: "914.4 mm (36 in)", gt5: "1.52 m (60 in)", gt7: "1600 mm (62 in)" },
  { label: "Cut height", gt3: "26–114 mm (1–4.5 in)", gt5: "26–152 mm (1–6 in)", gt7: "5–50 mm (0.197–1.97 in)" },
  { label: "Weight incl. batteries", gt3: "450 kg (992 lb)", gt5: "850 kg (1,873.93 lb)", gt7: "900 kg (1,984 lb)" },
  { label: "Battery", gt3: "13 kWh", gt5: "26 kWh", gt7: "26 kWh" },
  { label: "Runtime", gt3: "4–6 h", gt5: "8–10 h", gt7: "10–12 h (non-collection mode)" },
  { label: "Coverage per charge", gt3: "20,000 m² (5 acres)", gt5: "65,000 m² (16 acres)", gt7: "70,000 m² (17 acres, non-collection mode)" },
  { label: "Hourly mowing rate", gt3: "Not published", gt5: "8,000 m²/h (2 acres/h)", gt7: "8,000 m²/h (2 acres/h)" },
  { label: "Charging", gt3: "4 h at 3 kW / 2 h at 6 kW", gt5: "8 h at 3 kW / 4 h at 6 kW", gt7: "8 h at 3 kW / 4 h at 6 kW" },
  { label: "Mowing slope", gt3: "Not published", gt5: "20°", gt7: "30°" },
  { label: "Collection behavior", gt3: "Automatic hopper emptying + breakpoint return", gt5: "Automatic hopper emptying + breakpoint return", gt7: "Hopper-full detection + task resumption (automatic dumping not published)" },
  { label: "Protection rating", gt3: "IP66", gt5: "IP66 (all-metal chassis)", gt7: "Not published" },
];
